// Клиентское зеркало server `TerrainHeightmap`. Используется в prediction.floorAt,
// чтобы предсказание не расходилось с сервером на плавных склонах (мы НЕ
// вокселизуем `terrain_*`, см. tools/build_level и LEVEL_PIPELINE.md).

export const TERRAIN_EMPTY_TOP = -1e20;

export interface TerrainHeightmap {
  ox: number;
  oy: number;
  cellSize: number;
  w: number;
  h: number;
  topZ: Float32Array;
}

let current: TerrainHeightmap | null = null;

export function getTerrainHeightmap(): TerrainHeightmap | null { return current; }
export function setTerrainHeightmap(hm: TerrainHeightmap | null): void { current = hm; }

/** Загружает terrain.json (формат build_level/static_export.go) с сервера. */
export async function fetchTerrainHeightmap(
  origin: string, sceneId: string, file: string,
): Promise<TerrainHeightmap | null> {
  const url = `${origin}/api/level-asset?scene=${encodeURIComponent(sceneId)}` +
    `&file=${encodeURIComponent(file)}`;
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) return null;
  const j = await r.json() as {
    ox: number; oy: number; cell_size: number; w: number; h: number; top_z_b64: string;
  };
  if (!j || j.w <= 0 || j.h <= 0 || j.cell_size <= 0) return null;
  const bin = Uint8Array.from(atob(j.top_z_b64), (c) => c.charCodeAt(0));
  const expect = j.w * j.h * 4;
  if (bin.byteLength !== expect) {
    console.warn(`[terrain] bad heightmap size ${bin.byteLength} != ${expect}`);
    return null;
  }
  const dv = new DataView(bin.buffer, bin.byteOffset, bin.byteLength);
  const top = new Float32Array(j.w * j.h);
  for (let i = 0; i < top.length; i++) {
    top[i] = dv.getFloat32(i * 4, true);
  }
  return { ox: j.ox, oy: j.oy, cellSize: j.cell_size, w: j.w, h: j.h, topZ: top };
}

/** Зеркало `TerrainHeightmap.FloorAt` из server scene_loader.go. */
export function terrainFloorAt(
  hm: TerrainHeightmap | null,
  cx: number, cy: number, r: number, maxFloorZ: number,
): number {
  if (!hm) return TERRAIN_EMPTY_TOP;
  const cs = hm.cellSize;
  let bxMin = Math.floor((cx - r - hm.ox) / cs);
  let bxMax = Math.floor((cx + r - hm.ox) / cs);
  let byMin = Math.floor((cy - r - hm.oy) / cs);
  let byMax = Math.floor((cy + r - hm.oy) / cs);
  if (bxMin < 0) bxMin = 0;
  if (byMin < 0) byMin = 0;
  if (bxMax >= hm.w) bxMax = hm.w - 1;
  if (byMax >= hm.h) byMax = hm.h - 1;
  if (bxMin > bxMax || byMin > byMax) return TERRAIN_EMPTY_TOP;
  const r2 = r * r;
  let best = TERRAIN_EMPTY_TOP;
  for (let by = byMin; by <= byMax; by++) {
    const cellCy = hm.oy + (by + 0.5) * cs;
    for (let bx = bxMin; bx <= bxMax; bx++) {
      const cellCx = hm.ox + (bx + 0.5) * cs;
      const dx = cx - clamp(cx, cellCx - cs * 0.5, cellCx + cs * 0.5);
      const dy = cy - clamp(cy, cellCy - cs * 0.5, cellCy + cs * 0.5);
      if (dx * dx + dy * dy >= r2) continue;
      const v = hm.topZ[bx + by * hm.w];
      if (v === TERRAIN_EMPTY_TOP) continue;
      if (v > maxFloorZ) continue;
      if (v > best) best = v;
    }
  }
  return best;
}

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
